<i18n>
{
  "fr": {
    "catchphrase": "Voyagez avec Amplitudes",
    "years": "Moins d'un an | {count} an | {count} ans",
    "ferryText": "Demander un devis Ferry",
    "flightText": "Réserver un vol"
  },
  "en": {
    "catchphrase": "Travel with Amplitudes",
    "years": "Less than a year old | {count} year old | {count} years old",
    "ferryText": "Request a Ferry quote",
    "flightText": "Book a flight"
  }
}
</i18n>

<template>
  <div id="flight-research">
    <!--  Step 1  -->
    <v-row>
      <v-spacer />
      <h1
        v-if="step === '1' || checkIsFerry"
        id="flight-catchphrase"
      >
        {{ $t('catchphrase') }}
      </h1>
      <v-spacer />
    </v-row>
    <div
      id="container-button"
      class="py-1"
    >
      <button
        v-if="!checkIsFerry"
        id="ferry-button-styles"
        @click="clickOnFerry"
      >
        <v-icon class="mr-1">
          mdi-ferry
        </v-icon>
        {{ $t('ferryText') }}
      </button>

      <button
        v-if="checkIsFerry"
        id="ferry-button-styles"
        @click="clickOnAircraft"
      >
        <v-icon class="mr-1">
          mdi-airplane-landing
        </v-icon>
        {{ $t('flightText') }}
      </button>
    </div>

    <transition name="fade">
      <BookingFerry
        v-if="checkIsFerry"
      />
    </transition>

    <transition name="research-bar">
      <FlightResearchBar
        v-if="(step === '1' || step === '2') && !checkIsFerry"
        id="flight-research-bar"
        :class="{'step-one-research-bar': step === '1',
                 'step-two-research-bar': step === '2'}"
      />
    </transition>

    <!--  Step 2  -->
    <transition name="search-results-filters">
      <FlightSearchResultsFilters
        v-if="step === '2' && !step2Loading && !checkIsFerry "
      />
    </transition>

    <transition name="search-results">
      <FlightSearchResults
        v-if="step === '2' && !step2Loading && !checkIsFerry"
        id="flight-search-results"
        class="my-8"
      />
    </transition>

    <transition name="search-results-loader">
      <FlightSearchResultsLoader
        v-if="step === '2' && step2Loading && !checkIsFerry"
        id="flight-search-results-loader"
      />
    </transition>

    <!--  Step 3  -->
    <transition name="passengers-information">
      <FlightPassengersInformation
        v-if="step === '3' && !step3Loading && !checkIsFerry"
        id="flight-passengers-information"
        class="my-8"
      />
    </transition>

    <transition name="passengers-information">
      <FlightPassengersInformationLoader
        v-if="step === '3' && step3Loading && !checkIsFerry"
        id="flight-passengers-information-loader"
        class="my-8"
      />
    </transition>

    <!--  Step 4  -->
    <transition name="order-summary">
      <FlightOrderSummary
        v-if="step === '4' && !step4Loading && !checkIsFerry"
        id="flight-order-summary"
        class="my-8"
      />
    </transition>

    <transition name="order-summary">
      <FlightOrderSummaryLoader
        v-if="step === '4' && step4Loading && !checkIsFerry"
        id="flight-order-summary-loader"
        class="my-8"
      />
    </transition>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters, mapState } from 'vuex'
import FlightResearchBar from '@/components/Flight/FlightResearchBar/FlightResearchBar'
import FlightSearchResultsFilters from '@/components/Flight/FlightSearchResultsFilters/FlightSearchResultsFilters'
import FlightSearchResults from '@/components/Flight/FlightSearchResults/FlightSearchResults'
import FlightSearchResultsLoader from '@/components/Flight/FlightSearchResults/FlightSearchResultsLoader'
import FlightPassengersInformation from '@/components/Flight/FlightPassengersInformation/FlightPassengersInformation'
import FlightPassengersInformationLoader from '@/components/Flight/FlightPassengersInformation/FlightPassengersInformationLoader'
import FlightOrderSummary from '@/components/Flight/FlightOrderSummary/FlightOrderSummary'
import FlightOrderSummaryLoader from '@/components/Flight/FlightOrderSummary/FlightOrderSummaryLoader'
import BookingFerry from '@/components/Ferry/BookingFerry'
import moment from 'moment'
import globalMixin from '@/mixins/global'

export default {
  name: 'Flight',
  components: {
    FlightResearchBar,
    FlightSearchResultsFilters,
    FlightSearchResults,
    FlightSearchResultsLoader,
    FlightPassengersInformation,
    FlightPassengersInformationLoader,
    FlightOrderSummary,
    FlightOrderSummaryLoader,
    BookingFerry
  },
  mixins: [globalMixin],
  data: () => ({
    checkIsFerry: false
  }),
  computed: {
    ...mapState({
      step: state => state.fb.step,
      step2Loading: state => state.fb.step2Loading,
      step3Loading: state => state.fb.step3Loading,
      step4Loading: state => state.fb.step4Loading
    }),
    ...mapGetters({
      loggedIn: 'general/loggedIn'
    })
  },
  created () {
    window.addEventListener('beforeunload', this.beforeUnloadListener)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.beforeUnloadListener)
  },
  mounted () {
    this.initClassTypes()
    this.setTravellers()
  },
  methods: {
    ...mapMutations({
      changeTravellers: 'fb/passengersInformation/changeTravellers'
    }),
    ...mapActions({
      initClassTypes: 'fb/searchParameters/initClassTypes',
      returnHomeAction: 'general/returnHome'
    }),
    clickOnFerry () {
      this.checkIsFerry = true
    },
    clickOnAircraft () {
      this.checkIsFerry = false
      this.returnHomeAction()
      this.routerPushHome()
    },
    beforeUnloadListener (event) {
      event.preventDefault()
      event.returnValue = 'Are you sure you want to exit?'
      return event.returnValue
    },
    /**
     * Parse the customer object and set the travellers in the store
     * @return {undefined}
     */
    setTravellers () {
      const customer = this.$store.state.general.customer
      const travellers = customer.family.map(entry => {
        const formatName = this.formatTravellerDisplayName(entry)
        return Object.assign({}, entry, { formatName })
      })
      this.changeTravellers(travellers)
    },

    /**
     * Format the traveller name with the help of i18n
     * @param entry {Object}
     * @return {string}
     */
    formatTravellerDisplayName (entry) {
      const now = moment()
      let age = moment(entry.birthday)
      age = now.diff(age, 'years')
      const years = this.$i18n.tc('years', age)
      return `${entry.first_name} ${entry.last_name} (${years})`
    }
  }
}
</script>

<style scoped>
  #flight-research {
    width: 1200px;
    margin-left: auto;
    margin-right: auto;
  }

  #flight-catchphrase {
    font-size: 36px;
    font-weight: 300;
    font-family: 'Raleway', sans-serif;
    text-transform: uppercase;
    color: white;
    margin-top: 40px;
    margin-bottom: 40px;
  }

  #container-button {
    display: flex;
    flex-direction: row;
    justify-content: end;
  }

  #ferry-button-styles {
    background-color: #EA4C89;
    border-radius: 8px;
    border-style: none;
    color: #FFFFFF;
    display: inline-block;
    font-size: 14px;
    font-weight: 600;
    height: 40px;
    line-height: 20px;
    padding: 10px 16px;
    text-align: center;
  }

  /* Research Bar */

  .research-bar-enter-active, .research-bar-leave-active {
    transition: opacity 0.4s;
  }
  .research-bar-enter, .research-bar-leave-to {
    opacity: 0;
  }
  .step-one-research-bar {
    height: 374px !important;
  }
  .step-two-research-bar {
    margin-top: 32px;
    animation: research-bar-shrink 0.5s;
  }
  @keyframes research-bar-shrink {
    0% {
      margin-top: 0;
      transform: translate3d(0, 134px, 0);
      height: 374px;
    }
    50% {
      height: 374px;
    }
    100% {
      margin-top: 0;
      transform: translate3d(0, 32px, 0);
      height: 172px;
    }
  }

  /*  Search Results Filters */

  .search-results-filters-enter-active {
    animation: search-results-filters 1s;
  }
  .search-results-filters-leave-active {
    transition: opacity 0.5s;
  }
  .search-results-filters-enter, .search-results-filters-leave-to {
    opacity: 0;
    height: 0;
  }
  @keyframes search-results-filters {
    0% {
      opacity: 0;
      height: 0;
    }
    50% {
      transform: translateY(50px);
      height: 0;
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 100;
    }
  }

  /*  Search Results */

  .search-results-enter-active {
    opacity: 0;
    height: 0;
    animation: search-results 1.5s;
  }
  .search-results-leave-active {
    transition: opacity 0.5s;
  }
  .search-results-enter, .search-results-leave-to {
    height: 0;
    opacity: 0;
  }
  @keyframes search-results {
    0% {
      opacity: 0;
      height: 0;
    }
    75% {
      transform: translateY(50px);
      opacity: 0;
      height: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 100;
    }
  }

  /*  Search Results Loader */

  .search-results-loader-enter-active {
    animation: search-results-loader 1s;
  }
  .search-results-loader-leave-active {
    transition: opacity 0.5s;
  }
  .search-results-loader-enter, .search-results-loader-leave-to {
    opacity: 0;
  }
  @keyframes search-results-loader {
    0% {
      opacity: 0;
      height: 0;
    }
    50% {
      transform: translateY(50px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 100;
    }
  }

  /* Passengers information */

  .passengers-information-enter-active {
    animation: passengers-information 1s;
  }
  .passengers-information-leave-active {
    transition: opacity 0.5s;
  }
  .passengers-information-enter, .passengers-information-leave {
    opacity: 0;
  }
  @keyframes passengers-information {
    0% {
      opacity: 0;
      height: 0;
    }
    50% {
      transform: translateY(50px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 100;
    }
  }

  /* Order summary */

  .order-summary-enter-active {
    animation: passengers-information 1s;
  }
  .order-summary-leave-active {
    transition: opacity 0.5s;
  }
  .order-summary-enter, .order-summary-leave {
    opacity: 0;
  }
  @keyframes order-summary {
    0% {
      opacity: 0;
      height: 0;
    }
    50% {
      transform: translateY(50px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 100;
    }
  }
</style>
